import React, { Suspense, lazy, useState } from 'react';
import { useLocation } from 'react-router-dom';
import LeftNav from './left-nav/LeftNav';
import FAQMainContent from '../pages/FAQMainContent';
import { isClientControlWorkspaceReturn } from '../../lib/clientControlWorkspaceNav';
import { useAuth } from '../../context/AuthContext';
import { isClinicalVoiceRole, isTherapistVoiceCommandsEnabled } from '../../voice/types';

const TherapistVoiceHost = lazy(() => import('../../voice/TherapistVoiceHost'));

interface GlobalLayoutProps {
  children: React.ReactNode;
  showLeftNav?: boolean;
}

const GlobalLayout: React.FC<GlobalLayoutProps> = ({
  children,
  showLeftNav = true,
}) => {
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [currentContent, setCurrentContent] = useState<string | null>(null);
  const location = useLocation();
  const { user } = useAuth();
  const isClinicalRole = ['THERAPIST', 'INTERNS', 'CLINICIAN', 'ORG_ADMIN'].includes(
    (user?.role || '').toUpperCase(),
  );
  const showVoiceHost =
    isTherapistVoiceCommandsEnabled() && isClinicalVoiceRole(user?.role);
  const searchParams = new URLSearchParams(location.search);
  const isClientControlMilestoneRoute =
    isClientControlWorkspaceReturn(searchParams) &&
    (location.pathname.startsWith('/milestone-tasks/') ||
      location.pathname.startsWith('/milestone-details/'));

  // Don't show left nav on certain pages
  const hideLeftNavPages = [
    '/',
    '/organizations',
    '/parent-referral',
    '/org-admin',
    '/org-referral',
    '/therapist-choose-plan',
    '/contact',
    '/demo',
    '/resources/visual-schedules',
    '/resources/sensory-activities',
    '/resources/communication-tools',
    '/questionnaire/demo',
    '/admin-dashboard',
    '/about-us',
    '/privacy-policy',
    '/terms-of-service',
    '/contact-us',
    '/how-it-works',
    '/support',
    '/profile-setup',
    '/kid-profile-form',
    '/parent-child-setup',
    '/social-stories/view',
  ];

  // Practice / therapist workspace: same full-width chrome as org admin (no parent-app left nav).
  const isOrgAdminRoute =
    location.pathname.startsWith('/org-admin-dashboard') ||
    location.pathname.startsWith('/therapy-goals') ||
    location.pathname.startsWith('/care-plan') ||
    location.pathname.startsWith('/client-plan-wizard') ||
    location.pathname.startsWith('/client-control') ||
    location.pathname.startsWith('/record-assessment');
  const shouldShowLeftNav =
    showLeftNav &&
    !isClinicalRole &&
    !hideLeftNavPages.some(
      (page) => location.pathname === page || location.pathname.startsWith(`${page}/`),
    ) &&
    !isOrgAdminRoute &&
    !isClientControlMilestoneRoute;

  // Reset content when navigating to different routes
  React.useEffect(() => {
    setCurrentContent(null);
    setIsSidebarOpen(false);
  }, [location.pathname]);

  const handleContentChange = (content: string) => {
    setCurrentContent(content);
  };

  const renderMainContent = () => {
    if (currentContent === 'faq') {
      return <FAQMainContent />;
    }
    return children;
  };

  const withVoice = (node: React.ReactNode) => {
    if (!showVoiceHost) return <>{node}</>;
    return (
      <Suspense fallback={null}>
        <TherapistVoiceHost>{node}</TherapistVoiceHost>
      </Suspense>
    );
  };

  if (!shouldShowLeftNav) {
    return withVoice(children);
  }

  return withVoice(
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-purple-50">
      <div className="flex min-h-screen">
        <div className="hidden flex-shrink-0 lg:block">
          <LeftNav isOpen onContentChange={handleContentChange} />
        </div>

        <button
          type="button"
          onClick={() => setIsSidebarOpen((open) => !open)}
          className="fixed left-3 top-[76px] z-[1100] flex h-10 w-10 items-center justify-center rounded-lg border border-gray-200 bg-white text-gray-700 shadow-lg transition hover:bg-gray-50 lg:hidden"
          aria-label="Open app menu"
        >
          <span className="sr-only">Open app menu</span>
          <div className="flex flex-col gap-1" aria-hidden>
            <span className="block h-0.5 w-5 bg-current" />
            <span className="block h-0.5 w-5 bg-current" />
            <span className="block h-0.5 w-5 bg-current" />
          </div>
        </button>

        {isSidebarOpen ? (
          <div
            className="fixed bottom-0 left-0 right-0 top-[68px] z-40 bg-black/40 lg:hidden"
            onClick={() => setIsSidebarOpen(false)}
            aria-hidden
          />
        ) : null}

        <div
          className={`fixed bottom-0 left-0 top-[68px] z-[50] w-[min(17.5rem,88vw)] overflow-y-auto shadow-2xl transition-transform duration-300 lg:hidden ${
            isSidebarOpen ? 'translate-x-0' : 'pointer-events-none -translate-x-full'
          }`}
        >
          <LeftNav
            isOpen
            onClose={() => setIsSidebarOpen(false)}
            onContentChange={handleContentChange}
          />
        </div>

        <div className="min-w-0 flex-1 pl-14 pt-2 lg:pl-0 lg:pt-0">{renderMainContent()}</div>
      </div>
    </div>,
  );
};

export default GlobalLayout;
